"use client";
import {
  buildOdd,
  buildPaymentMethod,
  CheckoutIntent,
  formatAmountCents,
  GameAlignmentType,
  GameOdds,
  LineType,
  MarketplaceEventType,
  PaymentMethod,
  PaymentRail,
  RecentPick as RecentPickType,
} from "common";
import React, { createContext, useContext } from "react";
import RecentPick, { RecentPickState } from "../bettor/recent-pick";
import {
  loadStripe,
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import mlClient from "@/ml-client";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useAuth } from "@clerk/clerk-react";
import Link from "next/link";
import Button from "../primitives/button";
import { usePickSlip } from "./create-pick";
import assert from "assert";
import CreatedPick from "./created";
import PickSportsbooks, { getSportsbookEvents } from "./sportsbooks";
import Icon from "../primitives/icon";
import { useOddsFormat } from "@/utils/providers/odds-format";
import { formatOdd } from "@/utils";
import RadioGroup from "../primitives/radio-group";
import Checkbox from "../primitives/checkbox";
import CardBrand from "../primitives/card-brand";
import Info from "../primitives/info";
import Accordion, { AccordionItem } from "../primitives/accordion";
import { PickProvider } from "@/utils/providers/pick";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  Checkout,
  CheckoutButton,
  CheckoutStatus,
} from "@coinbase/onchainkit/checkout";
import { useBettor } from "@/utils/providers/bettor";
import { useRoom } from "@/utils/providers/room";
import { CheckedState } from "@radix-ui/react-checkbox";
const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
);

export interface PickCheckoutContextType {
  checkoutIntent: CheckoutIntent | null;
  setCheckoutIntent: React.Dispatch<
    React.SetStateAction<CheckoutIntent | null>
  >;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  loading: PickCheckoutStep | null;
  setLoading: React.Dispatch<React.SetStateAction<PickCheckoutStep | null>>;
  selectedPaymentRail: PaymentRail | null;
  setSelectedPaymentRail: React.Dispatch<
    React.SetStateAction<PaymentRail | null>
  >;
  waiting: boolean;
  setWaiting: React.Dispatch<React.SetStateAction<boolean>>;
  step: PickCheckoutStep;
  setStep: React.Dispatch<React.SetStateAction<PickCheckoutStep>>;
  revealedPick: RecentPickType | null;
  setRevealedPick: React.Dispatch<React.SetStateAction<RecentPickType | null>>;
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
}
export const PickCheckoutContext =
  createContext<PickCheckoutContextType | null>(null);

export const usePickCheckout = () => {
  const context = useContext(PickCheckoutContext);
  if (!context) {
    throw new Error(
      "usePickCheckout must be used within a PickCheckoutProvider"
    );
  }
  return context;
};

enum PickCheckoutStep {
  Overview,
  Payment,
  Review,
  Reveal,
}

export default function PickCheckout() {
  const slip = usePickSlip();
  const [checkoutIntent, setCheckoutIntent] =
    React.useState<CheckoutIntent | null>(null);
  const [loading, setLoading] = React.useState<PickCheckoutStep | null>(
    PickCheckoutStep.Overview
  );
  const [step, setStep] = React.useState<PickCheckoutStep>(
    PickCheckoutStep.Overview
  );
  const [waiting, setWaiting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedPaymentRail, setSelectedPaymentRail] =
    React.useState<PaymentRail | null>(null);
  const [paymentMethod, setPaymentMethod] =
    React.useState<PaymentMethod | null>(null);
  const [revealedPick, setRevealedPick] = React.useState<RecentPickType | null>(
    null
  );

  // Change button whenever the step or loading state changes.
  React.useEffect(() => {
    if (error) {
      slip.setButton(
        <Button
          className="!justify-center min-w-[146px]"
          onClick={() => slip.removeCheckoutPick()}
        >
          Cancel
        </Button>
      );
    }
    if (loading !== null) {
      slip.setButton(
        <Button className="!justify-center min-w-[146px]" disabled={true}>
          Loading...
        </Button>
      );
    }
    if (waiting) {
      slip.setButton(
        <Button className="!justify-center min-w-[146px]" disabled={true}>
          Processing...
        </Button>
      );
    }
  }, [loading, waiting, error]);

  assert(slip?.checkoutPick, "Pick isn't for sale.");

  const options: StripeElementsOptionsMode = {
    mode: "payment",
    amount: (checkoutIntent?.amount || 0) + (checkoutIntent?.tax || 0),
    currency: "usd",
    paymentMethodCreation: "manual",
    appearance: {
      theme: "flat",
    },
  };

  const render = (): React.ReactNode => {
    if (error) {
      return (
        <div className="flex flex-col gap-4 bg-surface-700 py-12 px-4 rounded-md">
          <Info.Block variant="error" title="Sorry Sport!" message={error} />
        </div>
      );
    }
    if (waiting) {
      <div className="flex flex-col gap-4 bg-surface-700 py-12 px-4 rounded-md">
        <Info.Block
          variant="loading"
          title="Waiting..."
          message="Confirming your order."
        />
      </div>;
    }
    switch (step) {
      case PickCheckoutStep.Overview:
        return <Overview />;
      case PickCheckoutStep.Payment:
        return (
          <Elements stripe={stripePromise} options={options}>
            <Payment />
          </Elements>
        );
      case PickCheckoutStep.Review:
        return <Review />;
      case PickCheckoutStep.Reveal:
        return <Reveal />;
      default:
        return <>Something rather unexpected happened. Contact an admin.</>;
    }
  };
  return (
    <PickCheckoutContext.Provider
      value={{
        checkoutIntent,
        setCheckoutIntent,
        error,
        setError,
        loading,
        setLoading,
        selectedPaymentRail,
        setSelectedPaymentRail,
        waiting,
        setWaiting,
        step,
        setStep,
        paymentMethod,
        setPaymentMethod,
        revealedPick,
        setRevealedPick,
      }}
    >
      {step !== PickCheckoutStep.Reveal && (
        <Steps />
      )}
      {render()}
    </PickCheckoutContext.Provider>
  );
}

/** Step 1 - Overview
 * Gets or creates a checkout intent.
 */
function Overview() {
  const checkout = usePickCheckout();
  const slip = usePickSlip();
  const { getToken } = useAuth();
  assert(slip?.checkoutPick, "Pick isn't for sale.");

  /** Any time the pick id changes, get the checkout intent.
   * First will attempt to get the intent from localStorage and will fallback to the server.
   */
  const createCheckoutIntent = async () => {
    assert(slip?.checkoutPick, "Pick isn't for sale.");
    assert(slip?.checkoutPick.id, "Pick is missing id.");
    try {
      checkout.setLoading(PickCheckoutStep.Overview);
      const token = await getToken();
      const result = await mlClient.post(
        "/marketplace/checkout-intent",
        {
          pick_id: slip.checkoutPick.id,
          order_index: 0,
        },
        token
      );
      if (!result.ok) {
        checkout.setError(result.message);
        return;
      }
      if (result.data) {
        const intent = result.data;
        checkout.setCheckoutIntent(intent);
        checkout.setRevealedPick({
          ...slip.checkoutPick,
          bettor: intent.metadata.pick.bettor,
          created_at: intent.metadata.pick.created_at,
        });
        // localStorage.setItem(localCheckoutIntentKey, JSON.stringify(intent));
        slip.setPrice(((intent.amount + intent.tax) / 100).toFixed(2));
      }
    } catch (err) {
      console.log("Error in createPaymentIntent:", err);
      checkout.setError("Something unexpected happened. Contact an admin.");
    } finally {
      checkout.setLoading(null);
    }
  };

  React.useEffect(() => {
    if (!checkout.checkoutIntent) {
      createCheckoutIntent();
    }
  }, [checkout.checkoutIntent, slip.checkoutPick.id]);

  // On render, change the button of the pick slip.
  React.useEffect(() => {
    slip.setButton(
      <Button
        className="!justify-center min-w-[146px]"
        onClick={() => {
          checkout.setStep(PickCheckoutStep.Payment);
        }}
        disabled={checkout.error ? true : false}
      >
        Continue
      </Button>
    );
  }, [checkout.loading, checkout.step, checkout.error]);

  if (checkout.loading === PickCheckoutStep.Overview) {
    return (
      <div className="flex flex-col gap-4 bg-surface-700 py-12 px-4 rounded-md">
        <Info.Block
          variant="loading"
          title="Loading..."
          message="Fetching more details."
        />
      </div>
    );
  }
  if (checkout.error) {
    return <>{checkout.error}</>;
  }

  assert(checkout.revealedPick);
  return (
    <div className="flex flex-col gap-4 bg-surface-700 p-4 rounded-md">
      <PickProvider pick={checkout.revealedPick}>
        <RecentPick
          remove={slip.removeCheckoutPick}
          state={RecentPickState.Checkout}
        />
      </PickProvider>
      <OddComparison />
      <Total />
      <div className="text-secondary text-center text-body-sm leading-6 max-w-[280px] mx-auto">
        By completing your purchase, you agree to our{" "}
        <Link href="/terms-conditions" className="underline">
          Terms & Conditions
        </Link>{" "}
        and{" "}
        <Link href="/privacy-policy" className="underline">
          Privacy Policy.
        </Link>
      </div>
    </div>
  );
}

/** Step 2 - Payment
 * Provides a list of saved payment methods and allows you add new ones.
 * Once finished, collects your details to attach it to the intent on the server.
 */
function Payment() {
  const { bettor } = useBettor();
  const wsData = useRoom();
  const slip = usePickSlip();
  const checkout = usePickCheckout();
  const stripe = useStripe();
  const elements = useElements();
  const { getToken } = useAuth();

  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>(
    checkout.checkoutIntent?.payment_methods || []
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    React.useState<PaymentMethod | null>(() => {
      const selectedId = checkout.checkoutIntent?.selected_payment_method_id;
      let foundMethod = null;
      if (selectedId) {
        foundMethod = paymentMethods.find((pm) => pm.id === selectedId);
      }
      return (
        foundMethod ?? (paymentMethods.length > 0 ? paymentMethods[0] : null)
      );
    });
  const formattedBalance = formatAmountCents(
    bettor?.details.wallet_balance || 0
  );

  const [expressCheckoutReady, setExpressCheckoutReady] =
    React.useState<boolean>(false);
  const [showCoinbaseCheckout, setShowCoinbaseCheckout] =
    React.useState<boolean>(false);
  const [paymentFormComplete, setPaymentFormComplete] =
    React.useState<boolean>(false);
  const [savePaymentMethod, setSavePaymentMethod] =
    React.useState<CheckedState>(false);

  console.log("saving payment method", savePaymentMethod);

  const changePaymentMethod = (value: string) => {
    const pm: PaymentMethod | undefined = paymentMethods.find(
      (pm) => pm.id === value
    );
    if (!pm) return;
    setSelectedPaymentMethod(pm);
  };

  const deletePaymentMethod = async (paymentMethodId: string) => {
    try {
      const token = await getToken();
      const result = await mlClient.delete(
        `/marketplace/payment-methods/${paymentMethodId}`,
        token
      );
      if (result.ok) {
        setPaymentMethods((prev) =>
          prev.filter((pm) => pm.id !== paymentMethodId)
        );
        if (selectedPaymentMethod?.id === paymentMethodId) {
          setSelectedPaymentMethod(null);
        }
      } else {
        checkout.setError("Failed to delete payment method. Try again.");
      }
    } catch (err) {
      console.error("Error deleting payment method:", err);
      checkout.setError("Something unexpected happened. Contact an admin.");
    }
  };
  const updateCheckoutIntent = React.useCallback(async () => {
    console.log("updating checkout intent");
    assert(slip?.checkoutPick, "Pick isn't for sale.");
    let usingPaymentMethod: PaymentMethod | null =
      selectedPaymentMethod ?? null;
    try {
      if (!usingPaymentMethod || paymentFormComplete) {
        if (!stripe || !elements) {
          checkout.setError("Stripe not loaded.");
          return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
          console.error("Form validation failed:", submitError.message);
          checkout.setError("Payment element failed to validate.");
          return;
        }
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
        });

        if (error) {
          console.error("Error creating payment method:", error.message);
          checkout.setError("Error creating payment method.");
          return;
        }
        usingPaymentMethod = buildPaymentMethod(paymentMethod);
        console.log("New payment method created:", paymentMethod);
      }
      assert(usingPaymentMethod !== null, "Failed to collect payment method.");
      checkout.setSelectedPaymentRail(PaymentRail.Stripe);
      checkout.setLoading(PickCheckoutStep.Review);
      const token = await getToken();
      const result = await mlClient.put(
        "/marketplace/checkout-intent",
        {
          selected_payment_method_id: usingPaymentMethod.id,
          order_index: 0,
          save_payment_method: savePaymentMethod === true ? true : false,
        },
        token
      );
      if (result.ok && result.code === 201) {
        const newIntent = checkout.checkoutIntent
          ? {
              ...checkout.checkoutIntent,
              selected_payment_method_id: usingPaymentMethod.id,
            }
          : checkout.checkoutIntent;
        checkout.setCheckoutIntent(newIntent);
        checkout.setPaymentMethod(usingPaymentMethod);
        checkout.setStep(PickCheckoutStep.Review);
      }
    } catch (err) {
      console.error("Error updating checkout intent", err);
      checkout.setError("Something unexpected happened. Contact an admin.");
    } finally {
      checkout.setLoading(null);
    }
  }, [savePaymentMethod, selectedPaymentMethod, paymentFormComplete]);
  
  // On render, change the button of the pick slip.
  React.useEffect(() => {
    slip.setButton(
      <Button
        className="!justify-center min-w-[146px]"
        onClick={updateCheckoutIntent}
        disabled={!paymentFormComplete && !selectedPaymentMethod}
      >
        Continue
      </Button>
    );
  }, [checkout.loading, checkout.step, paymentFormComplete, updateCheckoutIntent]);

  React.useEffect(() => {
    console.log("save payment method", savePaymentMethod);
  }, [savePaymentMethod]);

  React.useEffect(() => {
    if (!wsData.roomData) return;
    if (!checkout.waiting) return;

    const messageType = wsData.roomData.type as MarketplaceEventType;
    switch (messageType) {
      case "bitpay:invoice_paid": {
        checkout.setWaiting(false);
        const confirmBitpayPayment = async () => {
          try {
            console.log("BitPay onApprove triggered");
            checkout.setLoading(PickCheckoutStep.Reveal);
            const token = await getToken();
            const result = await mlClient.post(
              "/marketplace/initiate-payment",
              {
                order_index: 0,
                payment_rail: PaymentRail.Bitpay,
                confirm: true,
              },
              token
            );
            if (!result.ok) {
              checkout.setError(result.message);
              throw new Error(result.message);
            }
            checkout.setCheckoutIntent(result.data);
            console.log("checkout intent data", result.data);
            const newRevealedPick = checkout.revealedPick
              ? { ...checkout.revealedPick, type: result.data.revealed_pick }
              : null;
            checkout.setRevealedPick(newRevealedPick);
            localStorage.removeItem("checkoutPick");
            if (newRevealedPick) {
              slip.setJustPurchasedPicks((prev) => ({
                ...(prev || {}),
                [newRevealedPick.id]: { type: newRevealedPick.type },
              }));
              const sportsbookEvents = await getSportsbookEvents([
                newRevealedPick,
              ]);
              slip.setSportsbooks(sportsbookEvents);
            }
            checkout.setStep(PickCheckoutStep.Reveal);
          } catch (err) {
            console.error("Error confirming Bitpay payment:", err);
            throw err;
          } finally {
            checkout.setLoading(null);
            checkout.setWaiting(false);
          }
        };
        confirmBitpayPayment();
        break;
      }
      case "bitpay:invoice_failed": {
        checkout.setError("Your Bitpay order failed. Please contact support.");
        checkout.setWaiting(false);
        break;
      }
      default:
        return;
    }
  }, [wsData.roomData]);

  assert(slip?.checkoutPick, "Pick isn't for sale.");

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: "auto",
  };

  if (checkout.loading === PickCheckoutStep.Review) {
    return (
      <div className="flex flex-col gap-4 bg-surface-700 py-12 px-4 rounded-md">
        <Info.Block
          variant="loading"
          title="Processing..."
          message="Saving and validating payment methods."
        />
      </div>
    );
  }

  if (!stripe || !elements) {
    return;
  }

  const handlePaymentElementChange = (event: any) => {
    setPaymentFormComplete(event.complete);
  };

  console.log("payment form complete", paymentFormComplete);

  let paymentMethodItems = paymentMethods.map((pm, i) => {
    return {
      id: `payment-method-${i}`,
      label: (
        <div className="flex flex-row gap-2 items-center w-full justify-between">
          <div className="flex flex-row items-center gap-2">
            <CardBrand brand={pm.brand} />{" "}
            <span className="text-secondary-500">
              xxxx-<span className="text-secondary-300">{pm.last4}</span>
            </span>{" "}
            <span className="text-secondary-500">
              ({pm.exp_month}/{pm.exp_year})
            </span>
          </div>
          <Button
            variant="circle"
            color="tertiary"
            size="sm"
            onClick={async () => deletePaymentMethod(pm.id)}
            aria-label="Delete payment method"
          >
            <Icon name="bin" size="sm" />
          </Button>
        </div>
      ),
      value: pm.id,
    };
  });

  let accordionDefaultValue = "new";
  const accordionItems: AccordionItem[] = [];
  if (paymentMethodItems.length > 0) {
    accordionDefaultValue = "saved";
    accordionItems.push({
      label: "Saved Payment Methods",
      value: "saved",
      content: (
        <>
          <RadioGroup
            value={selectedPaymentMethod?.id}
            onValueChange={changePaymentMethod}
            label="payment-methods"
            items={paymentMethodItems}
          />
        </>
      ),
    });
  }
  accordionItems.push({
    label: "+ Add a new credit or debit card",
    value: "new",
    content: (
      <div className="flex flex-col gap-4">
        <PaymentElement
          id="payment-element"
          options={paymentElementOptions}
          onLoadError={(e) => checkout.setError(e.error.message || "")}
          onChange={handlePaymentElementChange}
        />
        <Checkbox
          id="save-payment-method"
          label="Save this payment method"
          checked={savePaymentMethod}
          onCheckedChange={(v) => {
            console.log("Save Payment method Checkbox toggled to:", v);
            if (v === "indeterminate") {
              setSavePaymentMethod(false);
            } else {
              setSavePaymentMethod(v);
            }
          }}
          disabled={!paymentFormComplete}
        />
      </div>
    ),
  });

  return (
    <div className="flex flex-col gap-4 bg-surface-700 p-4 rounded-md">
      <div className="pt-2 mb-2">
        <div className="text-title-lg-bold uppercase">Payment Method</div>
        <div className="text-body-sm text-secondary">
          All transactions are secure and encrypted.<br></br>Choose how you'd
          like to pay.
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <PayPalScriptProvider
          options={{
            clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID as string,
            disableFunding: "paylater,card,venmo",
          }}
        >
          <PayPalButtons
            style={{ layout: "vertical", height: 45, tagline: false }}
            createOrder={async () => {
              try {
                const token = await getToken();
                const result = await mlClient.post(
                  "/marketplace/initiate-payment",
                  {
                    order_index: 0,
                    payment_rail: PaymentRail.Paypal,
                    confirm: false,
                  },
                  token
                );
                if (!result.ok) {
                  checkout.setError(result.message);
                  throw new Error(result.message);
                }
                return result.data.paypal_order_id;
              } catch (err) {
                console.error("Error creating PayPal order:", err);
                throw err;
              }
            }}
            onApprove={async (data, actions) => {
              try {
                console.log("PayPal onApprove triggered");
                checkout.setLoading(PickCheckoutStep.Reveal);
                const token = await getToken();
                const result = await mlClient.post(
                  "/marketplace/initiate-payment",
                  {
                    order_index: 0,
                    payment_rail: PaymentRail.Paypal,
                    confirm: true,
                  },
                  token
                );
                if (!result.ok) {
                  checkout.setError(result.message);
                  throw new Error(result.message);
                }
                checkout.setCheckoutIntent(result.data);
                console.log("checkout intent data", result.data);
                const newRevealedPick = checkout.revealedPick
                  ? {
                      ...checkout.revealedPick,
                      type: result.data.revealed_pick,
                    }
                  : null;
                checkout.setRevealedPick(newRevealedPick);
                localStorage.removeItem("checkoutPick");
                if (newRevealedPick) {
                  slip.setJustPurchasedPicks((prev) => ({
                    ...(prev || {}),
                    [newRevealedPick.id]: { type: newRevealedPick.type },
                  }));
                  const sportsbookEvents = await getSportsbookEvents([
                    newRevealedPick,
                  ]);
                  slip.setSportsbooks(sportsbookEvents);
                }
                checkout.setStep(PickCheckoutStep.Reveal);
                window.close();
              } catch (err) {
                console.error("Error confirming PayPal payment:", err);
                throw err;
              } finally {
                checkout.setLoading(null);
              }
            }}
          />
        </PayPalScriptProvider>
    
          <Checkout
            chargeHandler={async () => {
              try {
                const token = await getToken();
                const result = await mlClient.post(
                  "/marketplace/initiate-payment",
                  {
                    order_index: 0,
                    payment_rail: PaymentRail.Coinbase,
                    confirm: false,
                  },
                  token
                );
                if (!result.ok) {
                  checkout.setError(result.message);
                  return;
                }
                return result.data.coinbase_order_id;
              } catch (err) {
                console.error("Error creating Coinbase order:", err);
                checkout.setError("Error creating Coinbase order.");
              }
            }}
            onStatus={async (status) => {
              try {
                const { statusName, statusData } = status;

                if (statusName !== "success") {
                  console.log("Coinbase checkout status:", statusName);
                  return;
                }

                const chargeId = statusData.chargeId;
                if (!chargeId) {
                  checkout.setError("Missing chargeId from Coinbase status");
                  return;
                }

                console.log("Coinbase onStatus triggered");
                checkout.setLoading(PickCheckoutStep.Reveal);
                const token = await getToken();
                const result = await mlClient.post(
                  "/marketplace/initiate-payment",
                  {
                    order_index: 0,
                    payment_rail: PaymentRail.Coinbase,
                    confirm: true,
                  },
                  token
                );
                if (!result.ok) {
                  checkout.setError(result.message);
                  throw new Error(result.message);
                }
                checkout.setCheckoutIntent(result.data);
                console.log("checkout intent data", result.data);
                const newRevealedPick = checkout.revealedPick
                  ? {
                      ...checkout.revealedPick,
                      type: result.data.revealed_pick,
                    }
                  : null;
                checkout.setRevealedPick(newRevealedPick);
                localStorage.removeItem("checkoutPick");
                if (newRevealedPick) {
                  slip.setJustPurchasedPicks((prev) => ({
                    ...(prev || {}),
                    [newRevealedPick.id]: { type: newRevealedPick.type },
                  }));
                  const sportsbookEvents = await getSportsbookEvents([
                    newRevealedPick,
                  ]);
                  slip.setSportsbooks(sportsbookEvents);
                }
                checkout.setStep(PickCheckoutStep.Reveal);
                window.close();
              } catch (err) {
                console.error("Error confirming Coinbase payment:", err);
                checkout.setError("Error confirming Coinbase payment.");
              } finally {
                checkout.setLoading(null);
              }
            }}
          >
            <CheckoutButton className="bg-blue mt-0" coinbaseBranded text="Pay with Crypto" />
            <CheckoutStatus />
          </Checkout>

        <button
          className="flex justify-center items-center h-12 w-full hover:bg-surface-500 bg-surface-600 border border-surface-600"
          onClick={async () => {
            checkout.setLoading(PickCheckoutStep.Reveal);
            checkout.setWaiting(true);
            try {
              const token = await getToken();
              const result = await mlClient.post(
                "/marketplace/initiate-payment",
                {
                  order_index: 0,
                  payment_rail: PaymentRail.Bitpay,
                  confirm: false,
                },
                token
              );

              if (!result.ok) {
                checkout.setError(result.message);
                checkout.setWaiting(false);
                return;
              }

              const invoiceUrl = result.data.bitpay_invoice_url;
              window.open(invoiceUrl, "_blank");
              return;
            } catch (err) {
              console.error("BitPay initiation failed", err);
              checkout.setError("BitPay initiation failed.");
              checkout.setWaiting(false);
            } finally {
              checkout.setLoading(null);
            }
          }}
        >
          <Bitpay />
        </button>
        <button
          className="gap-1 text-secondary-300 text-title-medium uppercase flex justify-center items-center h-12 w-full hover:bg-surface-500 bg-surface-600 border border-surface-600 mt-1"
          onClick={() => {
            checkout.setSelectedPaymentRail(PaymentRail.Moneyline);
            checkout.setStep(PickCheckoutStep.Review);
          }}
        >
          <Icon
            name="moneyline"
            size="sm"
            variant="circle"
            className="bg-dark"
          />
          <span className="text-"></span>
          Moneyline (${formattedBalance.whole}.{formattedBalance.decimal})
        </button>
      </div>
      <div className="flex flex-row items-center justify-center w-full gap-4 mt-2">
        <div className="w-full h-px bg-secondary-600"></div>
        <div className="text-body-sm text-secondary-500 min-w-[100px] text-center">
          or pay with a card
        </div>
        <div className="w-full h-px bg-secondary-600"></div>
      </div>
      <Accordion
        defaultValue={accordionDefaultValue}
        type="single"
        variant="form"
        items={accordionItems}
      />
      <Total />
    </div>
  );
}

/** Step 3 - Review
 * Shows a summary of the payment to be made
 * along with the selected payment method.
 */
function Review() {
  const { bettor } = useBettor();
  const slip = usePickSlip();
  const checkout = usePickCheckout();
  const { getToken } = useAuth();
  const formattedBalance = formatAmountCents(
    bettor?.details.wallet_balance || 0
  );
  const confirmPayment = async () => {
    assert(slip?.checkoutPick, "Pick isn't for sale.");
    try {
      checkout.setLoading(PickCheckoutStep.Reveal);
      const token = await getToken();
      const result = await mlClient.post(
        "/marketplace/initiate-payment",
        {
          order_index: 0,
          payment_rail: checkout.selectedPaymentRail,
          confirm: true,
        },
        token
      );
      if (!result.ok) {
        console.error(result.message);
        checkout.setError(result.message);
        return;
      }
      if (result.ok && result.code === 201) {
        checkout.setCheckoutIntent(result.data);
        console.log("checkout intent data", result.data);
        const newRevealedPick = checkout.revealedPick
          ? { ...checkout.revealedPick, type: result.data.revealed_pick }
          : null;
        checkout.setRevealedPick(newRevealedPick);
        localStorage.removeItem("checkoutPick");
        if (newRevealedPick) {
          slip.setJustPurchasedPicks((prev) => ({
            ...(prev || {}),
            [newRevealedPick.id]: { type: newRevealedPick.type },
          }));
          const sportsbookEvents = await getSportsbookEvents([newRevealedPick]);
          slip.setSportsbooks(sportsbookEvents);
        }
        checkout.setStep(PickCheckoutStep.Reveal);
      }
    } catch (err) {
      console.error("Error confirming payment.", err);
      checkout.setError("Something unexpected happened. Contact an admin.");
    } finally {
      checkout.setLoading(null);
    }
  };

  // On render, change the button of the pick slip.
  React.useEffect(() => {
    slip.setButton(
      <Button
        className="!justify-center min-w-[146px]"
        onClick={() => confirmPayment()}
      >
        Confirm & Pay
      </Button>
    );
  }, [checkout.loading, checkout.step]);

  if (checkout.loading === PickCheckoutStep.Reveal) {
    return (
      <div className="flex flex-col gap-4 bg-surface-700 py-12 px-4 rounded-md">
        <Info.Block
          variant="loading"
          title="Processing..."
          message="Get ready to see your pick!"
        />
      </div>
    );
  }

  assert(slip?.checkoutPick, "Pick isn't for sale.");
  assert(checkout.revealedPick);
  if (checkout.selectedPaymentRail === PaymentRail.Stripe) {
    assert(checkout.paymentMethod);
  }
  return (
    <div className="flex flex-col gap-4 bg-surface-700 p-4 rounded-md">
      <PickProvider pick={checkout.revealedPick}>
        <RecentPick
          remove={slip.removeCheckoutPick}
          state={RecentPickState.Checkout}
        />
      </PickProvider>
      <OddComparison />
      <div className="text-title-lg-bold uppercase mt-2">Payment Summary</div>
      {checkout.selectedPaymentRail === PaymentRail.Stripe &&
      checkout.paymentMethod ? (
        <>
          <div className="text-body-sm text-secondary">
            Make sure your information is correct.
          </div>
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row items-center gap-2">
              <CardBrand brand={checkout.paymentMethod.brand} />{" "}
              <span className="text-secondary-500">
                xxxx-
                <span className="text-secondary-300">
                  {checkout.paymentMethod.last4}
                </span>
              </span>{" "}
              <span className="text-secondary-500">
                ({checkout.paymentMethod.exp_month}/
                {checkout.paymentMethod.exp_year})
              </span>
            </div>
            <button
              className="link text-label-lg-bold text-secondary-300 uppercase"
              onClick={() => checkout.setStep(PickCheckoutStep.Payment)}
            >
              Change
            </button>
          </div>
        </>
      ) : (
        checkout.selectedPaymentRail === PaymentRail.Moneyline && (
          <div className="flex flex-row items-center gap-2">
            <Icon
              name="moneyline"
              size="sm"
              variant="circle"
              className="bg-dark"
            />
            <span className="text-secondary text-body-sm">
              Moneyline Balance (${formattedBalance.whole}.
              {formattedBalance.decimal})
            </span>
          </div>
        )
      )}

      <Total />
    </div>
  );
}

function Reveal() {
  const slip = usePickSlip();
  const checkout = usePickCheckout();
  assert(slip.checkoutPick, "Must have a pick in cart.");

  // On render, change the button of the pick slip.
  React.useEffect(() => {
    slip.setButton(
      <Button
        className="!justify-center min-w-[146px]"
        onClick={() => {
          slip.setSlipOpen(false);
          slip.removeCheckoutPick();
        }}
      >
        Done
      </Button>
    );
  }, [checkout.loading, checkout.step]);

  console.log("revealed", checkout.revealedPick);
  assert(checkout.revealedPick);

  return (
    <div className="gap-6 grid grid-rows-[auto_auto] px-2 sm:px-3 pb-4 overflow-y-auto grow mt-2">
      <div className="flex flex-col gap-4">
        <CreatedPick pick={checkout.revealedPick} purchased />
      </div>
      <PickSportsbooks
        sportsbooks={slip.sportsbooks}
        activePick={checkout.revealedPick}
      />
    </div>
  );
}

function Steps() {
  const checkout = usePickCheckout();
  const disabled: boolean =
    checkout.error !== null || checkout.loading !== null;
  return (
    <div className="flex flex-row items-center gap-4 px-4 pb-4">
      <button
        className={`${
          checkout.step >= PickCheckoutStep.Overview
            ? "text-secondary-200"
            : "text-secondary-500"
        } transition-all ease-in-out duration-200 text-title-lg-bold uppercase`}
        onClick={() => {
          if (checkout.step >= PickCheckoutStep.Overview) {
            checkout.setStep(PickCheckoutStep.Overview);
          }
        }}
        disabled={disabled}
      >
        Overview
      </button>
      <div
        className={`${
          checkout.step > PickCheckoutStep.Overview
            ? "bg-secondary-200"
            : "bg-secondary-700"
        } transition-all ease-in-out duration-200 w-full h-px`}
      ></div>
      <button
        className={`${
          checkout.step >= PickCheckoutStep.Payment
            ? "text-secondary-200"
            : "text-secondary-500"
        } transition-all ease-in-out duration-200 text-title-lg-bold uppercase`}
        onClick={() => {
          if (checkout.step >= PickCheckoutStep.Overview) {
            checkout.setStep(PickCheckoutStep.Payment);
          }
        }}
        disabled={disabled}
      >
        Payment
      </button>
      <div
        className={`${
          checkout.step > PickCheckoutStep.Payment
            ? "bg-secondary-200"
            : "bg-secondary-700"
        } transition-all ease-in-out duration-200 w-full h-px`}
      ></div>
      <button
        className={`${
          checkout.step >= PickCheckoutStep.Review
            ? "text-secondary-200"
            : "text-secondary-500"
        } transition-all ease-in-out duration-200 text-title-lg-bold uppercase`}
        onClick={() => {
          if (
            checkout.step >= PickCheckoutStep.Review ||
            checkout.paymentMethod
          ) {
            checkout.setStep(PickCheckoutStep.Review);
          }
        }}
        disabled={disabled}
      >
        Review
      </button>
    </div>
  );
}

function Total() {
  const checkout = usePickCheckout();
  const subtotal = ((checkout.checkoutIntent?.amount || 100) / 100).toFixed(2);
  const tax = (checkout.checkoutIntent?.tax || 0).toFixed(2);
  const total = (
    (checkout.checkoutIntent?.amount || 100) / 100 +
    (checkout.checkoutIntent?.tax || 0)
  ).toFixed(2);
  const currency = "USD";
  return (
    <div className="w-full bg-surface-500 rounded-md flex flex-col p-3 gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row text-title-sm justify-between items-center">
          <span>Subtotal</span>
          <span>${subtotal}</span>
        </div>
        <div className="flex flex-row text-title-sm justify-between items-center">
          <span>Tax</span>
          <span>${tax}</span>
        </div>
      </div>
      <div className="flex flex-row text-title-lg-bold uppercase justify-between items-center">
        <span>Total</span>
        <span>
          {currency} ${total}
        </span>
      </div>
    </div>
  );
}

function OddComparison() {
  const slip = usePickSlip();
  const checkout = usePickCheckout();
  const { oddsFormat } = useOddsFormat();
  assert(slip.checkoutPick);

  let sameOdds: boolean = false;
  const odds: GameOdds | undefined = checkout.checkoutIntent?.metadata.odds;
  assert(odds, "Checkout intent didn't deliver game odds.");
  const awayOdds = buildOdd(
    slip.checkoutPick.line,
    GameAlignmentType.Away,
    odds
  );
  const homeOdds = buildOdd(
    slip.checkoutPick.line,
    GameAlignmentType.Home,
    odds
  );
  const formattedAwayOdds = formatOdd(oddsFormat, awayOdds);
  const formattedHomeOdds = formatOdd(oddsFormat, homeOdds);
  switch (slip.checkoutPick.line) {
    case LineType.Moneyline:
      if (
        slip.checkoutPick.odds[0].payout === odds.awayMoneyline &&
        slip.checkoutPick.odds[1].payout === odds.homeMoneyline
      ) {
        sameOdds = true;
      }
      break;
    case LineType.Spread:
      if (
        slip.checkoutPick.odds[0].value === odds.awaySpread &&
        slip.checkoutPick.odds[1].value === odds.homeSpread &&
        slip.checkoutPick.odds[0].payout === odds.awaySpreadPayout &&
        slip.checkoutPick.odds[1].payout === odds.homeSpreadPayout
      ) {
        sameOdds = true;
      }
      break;
    case LineType.Total:
      if (
        slip.checkoutPick.odds[0].value === odds.overUnder &&
        slip.checkoutPick.odds[0].payout === odds.overPayout &&
        slip.checkoutPick.odds[1].payout === odds.underPayout
      ) {
        sameOdds = true;
      }
      break;
  }
  return (
    <div className="bg-secondary-300 rounded-md p-4">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col gap-y-1">
          <div className="text-label-lg-bold uppercase text-white">
            Current Game Odds
          </div>
          <div className="flex flex-row items-center gap-x-1">
            <Icon
              variant="circle"
              name={sameOdds ? "checkmark" : "alert-triangle"}
              size="xs"
              className={`${
                sameOdds ? "bg-primary-300" : "bg-orange"
              } fill-surface-700 text-surface-700`}
            />
            <div className="text-label-sm-medium text-secondary">
              Odds are currently {sameOdds ? "the same." : "different"}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="flex flex-row gap-x-3 justify-between items-center text-label-lg-bold">
            <span className="text-surface-700 uppercase min-w-[24px]">
              {slip.checkoutPick.game.teams[0].abbreviation}
            </span>
            <span
              className={`${sameOdds ? "text-primary-500" : "text-orange"}`}
            >
              {formattedAwayOdds?.value}
            </span>
            <span
              className={`${sameOdds ? "text-primary-500" : "text-orange"}`}
            >
              {formattedAwayOdds?.payout}
            </span>
          </div>
          <div className="flex flex-row gap-x-3 justify-between items-center text-label-lg-bold">
            <span className="text-surface-700 uppercase min-w-[24px]">
              {slip.checkoutPick.game.teams[1].abbreviation}
            </span>
            <span
              className={`${sameOdds ? "text-primary-500" : "text-orange"}`}
            >
              {formattedHomeOdds?.value}
            </span>
            <span
              className={`${sameOdds ? "text-primary-500" : "text-orange"}`}
            >
              {formattedHomeOdds?.payout}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bitpay() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="64"
      height="24"
      viewBox="150 137.133 500 175.735"
    >
      <g fill="#002855">
        <path d="M650 178.725h-26.01l-15.053 63.506-2.111 8.715c-1.452.396-2.904.791-4.357 1.057-2.641.527-5.413.791-8.316.791-3.302 0-5.941-.527-7.922-1.452-1.85-.924-3.302-2.243-4.094-4.093-.793-1.717-1.057-3.829-.925-6.205s.66-5.018 1.32-7.656l7.922-33.01 5.148-21.916h-26.538L557.05 229.16c-1.453 6.072-2.245 11.883-2.509 17.429-.265 5.544.66 10.298 2.509 14.655 1.849 4.225 5.148 7.525 9.638 10.033 4.489 2.509 10.959 3.695 19.146 3.695 5.678 0 10.694-.525 14.92-1.451.132 0 .396-.131.528-.131-1.188 5.28-3.565 9.504-7.263 12.807-3.829 3.301-9.375 5.017-16.769 5.017-3.169 0-6.073-.132-8.714-.526l-5.149 21.521c3.434.396 7.263.66 11.487.66 7.922 0 14.787-.924 20.728-2.642 5.942-1.849 10.961-4.488 15.316-8.186 4.225-3.697 7.79-8.318 10.694-13.73 2.904-5.545 5.148-12.017 6.865-19.674l17.956-75.521L650 178.725zM541.075 232.988c-1.585 6.602-2.113 13.335-1.453 19.937.66 6.733 3.301 16.899 5.81 22.048h-25.086c-3.036-5.147-2.903-7.656-3.563-9.24-3.302 2.641-6.865 4.885-10.826 6.602s-8.319 2.641-13.336 2.641c-5.81 0-10.826-1.057-14.919-3.037-4.093-1.979-7.526-4.752-10.167-8.186-2.642-3.434-4.488-7.395-5.679-12.016-1.188-4.621-1.716-9.506-1.716-14.786 0-8.056 1.453-15.58 4.227-22.709 2.903-7.13 6.864-13.337 11.883-18.617 5.017-5.281 17.296-17.031 37.364-17.031h40.27l-12.809 54.394zm-18.352-32.347c-12.146 0-14.392 0-19.407 2.51-3.565 1.848-6.471 4.621-8.979 7.789s-4.621 6.865-6.073 11.091c-1.452 4.225-2.245 8.581-2.245 13.071 0 5.545.926 10.033 2.773 13.334s5.281 4.885 10.298 4.885c2.642 0 5.148-.527 7.263-1.584 2.111-1.057 4.487-2.771 6.865-5.28.265-2.904.66-5.942 1.188-9.108.527-3.171 1.188-6.207 1.717-8.848l6.6-27.86M453.011 216.881c0 8.582-1.453 16.371-4.226 23.501-2.772 7.129-6.733 13.203-11.751 18.353s-10.958 9.11-17.956 12.016c-6.997 2.902-14.523 4.355-22.709 4.355-3.961 0-7.922-.396-12.016-1.056l-7.92 31.819h-26.012l30.235-127.014h34.724c6.604 0 12.279 1.057 16.9 3.035 4.753 1.98 8.582 4.754 11.751 8.188 3.037 3.433 5.28 7.525 6.732 12.146 1.588 4.49 2.248 9.376 2.248 14.657zm-63.772 36.044c1.979.527 4.489.66 7.394.66 4.621 0 8.715-.793 12.542-2.51 3.696-1.716 6.998-4.093 9.638-7.128 2.642-3.037 4.621-6.734 6.206-10.959 1.452-4.227 2.245-8.979 2.245-14.127 0-5.019-1.057-9.375-3.301-12.809-2.245-3.563-6.072-5.412-11.617-5.412h-10.3l-12.807 52.285zM334.579 252.925c-3.302 0-5.94-.528-7.922-1.452-1.849-.924-3.302-2.244-4.093-4.094-.793-1.716-1.057-3.828-.924-6.205.131-2.377.659-5.018 1.319-7.658l7.921-33.007h29.575l5.414-21.917h-29.708l6.865-27.727-27.727 4.355-17.56 74.202c-1.453 6.072-2.245 11.884-2.509 17.427-.265 5.545.66 10.301 2.509 14.656 1.848 4.225 5.149 7.525 9.638 10.034 4.489 2.509 10.959 3.696 19.145 3.696 5.678 0 10.694-.527 14.92-1.452.396-.134 1.056-.265 1.451-.396l5.414-22.578c-1.849.66-3.697 1.058-5.414 1.319-2.637.532-5.411.797-8.314.797zM268.432 178.725l-22.974 96.38h26.01l22.841-96.38zM297.082 167.633l3.961-16.768h-26.01l-3.961 16.768zM210.866 178.855c5.545 0 10.43 1.057 14.523 3.035 4.093 1.98 7.394 4.621 10.166 8.055 2.641 3.434 4.621 7.395 5.941 11.884 1.32 4.487 1.98 9.374 1.98 14.655 0 8.054-1.452 15.58-4.489 22.841-3.037 7.13-6.998 13.467-12.146 18.747-5.149 5.281-11.223 9.508-18.22 12.544-6.998 3.036-14.524 4.62-22.709 4.62-1.057 0-2.905 0-5.545-.131-2.641 0-5.677-.265-8.978-.793-3.433-.527-6.998-1.188-10.695-2.111A54.039 54.039 0 0 1 150 268.372l30.234-127.013 27.067-4.227-10.695 45.021c2.376-1.055 4.489-1.848 6.866-2.375 2.244-.658 4.753-.923 7.394-.923zm-22.71 74.73c4.093 0 7.922-.925 11.487-3.036a31.59 31.59 0 0 0 9.374-7.922c2.641-3.302 4.753-7.131 6.206-11.225 1.452-4.225 2.244-8.58 2.244-13.069 0-5.546-.924-9.901-2.773-13.071-1.848-3.169-5.677-4.621-10.694-4.621-1.585 0-3.301.133-5.942.793-2.641.527-4.885 1.849-6.997 3.696l-11.355 47.927c3.433.528 4.489.659 5.414.659.66-.131 1.716-.131 3.036-.131z" />
      </g>
    </svg>
  );
}
